import { PlusIcon, TrashIcon } from '@phosphor-icons/react';
import { Formik } from 'components/form/formik';
import toast from 'react-hot-toast';
import { useNavigate, useSearchParams } from 'react-router-dom';
import * as Yup from 'yup';
import { captureException } from '@sentry/react';
import { trackEvent } from '../../../utils/track-event';
import { useEffect, useRef, useState } from 'react';

import { getDisplayError } from '@utils/get-display-error';
import { Button } from 'components/button/Button';
import { Card } from 'components/Card';
import { SimpleSelectField } from 'components/select/SimpleSelectField';
import { SimpleSelect } from 'components/select/SimpleSelect';
import { CheckboxField } from 'components/checkbox/CheckboxField';
import { TextArea } from 'components/textarea/TextArea';
import { TextAreaField } from 'components/textarea/TextAreaField';
import { Input } from 'components/input/Input';
import { InputField } from 'components/input/InputField';
import { ToggleGroup } from 'components/toggle-group/ToggleGroup';
import { SupplierComboboxField, type Supplier } from 'src/app/supplier/SupplierComboboxField';
import { SupplierDriverComboboxField, type SupplierDriver } from 'src/app/supplier/SupplierDriverComboboxField';
import {
  MinimalOrderComboboxField,
  type MinimalOrder,
} from 'src/app/incoming-document/components/MinimalOrderComboboxField';
import { MultiFileUploaderButton, type IMinimalFile } from 'src/app/document/components/MultiFileUploaderButton';
import { FileUploaderButton } from 'src/app/document/components/FileUploaderButton';
import type { IDocument } from 'src/app/document/types';
import { useTrailer } from './trailerContext';
import { createInternalTrailerCheck } from '../models';
import type { TrailerCheckEventTypeType } from '../models';
import type { TrailerCheckDamageCategoryType } from '../endpoints/TrailerCheckEndpoint.types';
import type { Document } from 'src/types/enums';

const STATUS_OPTIONS = [
  { key: 'OK', name: 'OK' },
  { key: 'Damaged', name: 'Beschadigd' },
];

const DAMAGE_CATEGORY_OPTIONS = [
  { key: 'Tires', name: 'Banden' },
  { key: 'Brakes', name: 'Remmen' },
  { key: 'Lights', name: 'Verlichting' },
  { key: 'Body', name: 'Carrosserie' },
  { key: 'Floor', name: 'Vloer' },
  { key: 'Tarpaulin', name: 'Zeil' },
  { key: 'Doors', name: 'Deuren' },
  { key: 'Coupling', name: 'Koppeling' },
  { key: 'Other', name: 'Overig' },
];

interface DamageEntry {
  category: { key: string; name: string };
  remarks: string;
  photos: IMinimalFile[];
}

const createTrailerCheckSchema = Yup.object().shape({
  supplier: Yup.mixed().required('Vereist'),
  status: Yup.mixed().required('Vereist'),
  eventDate: Yup.string().required('Vereist'),
});

function todayDateString(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const ClearDriverOnSupplierChange: React.FC<{
  supplierId: number | string | undefined;
  setFieldValue: (field: string, value: any) => void;
}> = ({ supplierId, setFieldValue }) => {
  const prevIdRef = useRef(supplierId);
  useEffect(() => {
    if (supplierId !== prevIdRef.current) {
      prevIdRef.current = supplierId;
      setFieldValue('driver', null);
    }
  }, [supplierId, setFieldValue]);
  return null;
};

export const CreateTrailerCheckPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { trailer } = useTrailer();
  const [generalPhotos, setGeneralPhotos] = useState<IMinimalFile[]>([]);
  const [damages, setDamages] = useState<DamageEntry[]>([]);
  const [eventType, setEventType] = useState<TrailerCheckEventTypeType>('PickedUp');
  const [isActive, setIsActive] = useState(true);
  const [rentalStartDate, setRentalStartDate] = useState('');
  const [rentalEndDate, setRentalEndDate] = useState('');
  const [invoiceDocument, setInvoiceDocument] = useState<IDocument | null>(null);

  const prefillOrderId = searchParams.get('orderId');
  const prefillOrderNumber = searchParams.get('orderNumber');
  const prefillOrder: MinimalOrder | null = prefillOrderId
    ? { id: prefillOrderId, orderNumber: prefillOrderNumber || null, customerRef: '', customer: null }
    : null;

  const initialValues = {
    supplier: null as Supplier | null,
    driver: null as SupplierDriver | null,
    order: prefillOrder,
    status: STATUS_OPTIONS[0],
    cannotContinue: false,
    generalRemarks: '',
    eventDate: todayDateString(),
    locationName: '',
  };

  const addDamage = () => {
    setDamages((prev) => [...prev, { category: DAMAGE_CATEGORY_OPTIONS[0]!, remarks: '', photos: [] }]);
  };

  const removeDamage = (index: number) => {
    setDamages((prev) => prev.filter((_, i) => i !== index));
  };

  const updateDamage = (index: number, update: Partial<DamageEntry>) => {
    setDamages((prev) => prev.map((d, i) => (i === index ? { ...d, ...update } : d)));
  };

  const handleEventTypeChange = (nextEventType: TrailerCheckEventTypeType) => {
    setEventType(nextEventType);
    setIsActive(nextEventType === 'PickedUp');
  };

  return (
    <div className="px-4">
      <Formik
        initialValues={initialValues}
        validationSchema={createTrailerCheckSchema}
        onSubmit={async (values) => {
          if (values.status.key === 'Damaged' && damages.length === 0) {
            toast.error('Voeg minstens 1 schade toe bij status "Beschadigd"');
            return;
          }

          try {
            await createInternalTrailerCheck({
              trailerId: trailer.id,
              supplierId: values.supplier!.id as number,
              driverId: values.driver?.id,
              orderId: values.order?.id,
              status: values.status.key as 'OK' | 'Damaged',
              cannotContinue: values.cannotContinue,
              generalRemarks: values.generalRemarks,
              eventType,
              eventDate: new Date(values.eventDate).toISOString(),
              locationName: values.locationName || undefined,
              isActive,
              rentalStartDate: trailer.isRental && rentalStartDate ? rentalStartDate : undefined,
              rentalEndDate: trailer.isRental && rentalEndDate ? rentalEndDate : undefined,
              invoiceDocumentId: trailer.isRental ? invoiceDocument?.id : undefined,
              generalPhotoDocumentIds: generalPhotos.map((p) => p.id),
              damages: damages.map((d) => ({
                category: d.category.key as TrailerCheckDamageCategoryType,
                remarks: d.remarks,
                photoDocumentIds: d.photos.map((p) => p.id),
              })),
            });
            trackEvent('trailer_check_created', {
              trailer_id: trailer.id,
              status: values.status.key,
              damage_count: damages.length,
              cannot_continue: values.cannotContinue,
              event_type: eventType,
            });
            toast.success('Trailer check aangemaakt');
            navigate('..');
          } catch (err: any) {
            captureException(err);
            toast.error('Kon trailer check niet aanmaken: ' + getDisplayError(err));
          }
        }}
      >
        {({ handleSubmit, isSubmitting, values, setFieldValue }) => (
          <form onSubmit={handleSubmit} className="grid gap-4">
            <ClearDriverOnSupplierChange supplierId={values.supplier?.id} setFieldValue={setFieldValue} />
            <Card title="Nieuwe trailer check">
              <div className="mb-4">
                <div className="label-text mb-2">Type</div>
                <ToggleGroup
                  options={[
                    { value: 'PickedUp', label: 'Opgehaald' },
                    { value: 'DroppedOff', label: 'Afgezet' },
                  ]}
                  value={eventType}
                  onChange={handleEventTypeChange}
                />
              </div>
              <div className="mb-4">
                <div className="label-text mb-2">Actief</div>
                <ToggleGroup
                  options={[
                    { value: 'true', label: 'Ja' },
                    { value: 'false', label: 'Nee' },
                  ]}
                  value={isActive ? 'true' : 'false'}
                  onChange={(value) => setIsActive(value === 'true')}
                />
              </div>
              <InputField labelText="Datum" type="date" name="eventDate" />
              <SupplierComboboxField name="supplier" labelText="Vervoerder" />
              <SupplierDriverComboboxField
                name="driver"
                labelText="Chauffeur"
                supplierId={values.supplier?.id ?? null}
              />
              <MinimalOrderComboboxField name="order" labelText="Order" />
              <SimpleSelectField labelText="Status" items={[...STATUS_OPTIONS]} name="status" />
              <CheckboxField labelText="Kan niet doorrijden" name="cannotContinue" />
              <InputField labelText="Locatie" type="text" name="locationName" />
              <TextAreaField labelText="Opmerkingen" name="generalRemarks" />
              {trailer.isRental && (
                <>
                  <label>
                    <div className="label-text">Datum start huur</div>
                    <Input type="date" value={rentalStartDate} onChange={setRentalStartDate} />
                  </label>
                  <label>
                    <div className="label-text">Datum einde huur</div>
                    <Input type="date" value={rentalEndDate} onChange={setRentalEndDate} />
                  </label>
                  <div>
                    <div className="label-text mb-2">Factuur verhuurmaatschappij</div>
                    <FileUploaderButton
                      buttonText="Factuur uploaden"
                      title="Factuur verhuurmaatschappij"
                      file={invoiceDocument}
                      onSubmit={async (doc) => {
                        setInvoiceDocument(doc);
                      }}
                    />
                  </div>
                </>
              )}
            </Card>

            <Card title="Algemene foto's">
              <MultiFileUploaderButton
                buttonText={`Foto's (${generalPhotos.length})`}
                title="Algemene foto's"
                files={generalPhotos}
                uploadAsImages
                onSubmit={async (documents: Document[], close) => {
                  setGeneralPhotos((prev) => [
                    ...prev,
                    ...documents.map((d) => ({ id: d.id, name: d.name, hash: d.hash })),
                  ]);
                  close();
                }}
                unlinkDocument={async (id) => {
                  setGeneralPhotos((prev) => prev.filter((p) => p.id !== id));
                }}
              />
            </Card>

            {values.status.key === 'Damaged' && (
              <Card title="Schades">
                <div className="space-y-4">
                  {damages.map((damage, index) => (
                    <div key={index} className="border rounded p-4">
                      <div className="flex items-end gap-2 mb-2">
                        <div className="flex-1">
                          <div className="label-text">Categorie</div>
                          <SimpleSelect
                            items={DAMAGE_CATEGORY_OPTIONS}
                            selectedItem={damage.category}
                            onSelect={(item) => {
                              if (item) updateDamage(index, { category: item });
                            }}
                          />
                        </div>
                        <Button
                          color="danger"
                          onTrigger={() => removeDamage(index)}
                          iconLeft={<TrashIcon className="button-icon" />}
                        >
                          Verwijder
                        </Button>
                      </div>

                      <div className="mb-2">
                        <div className="label-text">Opmerkingen</div>
                        <TextArea
                          value={damage.remarks}
                          onChange={(value) => updateDamage(index, { remarks: value })}
                          rows={2}
                        />
                      </div>

                      <MultiFileUploaderButton
                        buttonText={`Foto's (${damage.photos.length})`}
                        title={`Foto's - ${damage.category.name}`}
                        files={damage.photos}
                        uploadAsImages
                        onSubmit={async (documents: Document[], close) => {
                          updateDamage(index, {
                            photos: [
                              ...damage.photos,
                              ...documents.map((d) => ({ id: d.id, name: d.name, hash: d.hash })),
                            ],
                          });
                          close();
                        }}
                        unlinkDocument={async (id) => {
                          updateDamage(index, { photos: damage.photos.filter((p) => p.id !== id) });
                        }}
                      />
                    </div>
                  ))}

                  <Button color="secondary" onTrigger={addDamage} iconLeft={<PlusIcon className="button-icon" />}>
                    Schade toevoegen
                  </Button>
                </div>
              </Card>
            )}

            <div>
              <Button
                type="submit"
                color="primary"
                isDisabled={isSubmitting}
                isLoading={isSubmitting}
                iconLeft={<PlusIcon className="button-icon" />}
              >
                Maak trailer check aan
              </Button>
            </div>
          </form>
        )}
      </Formik>
    </div>
  );
};
